import { TextField } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC, SyntheticEvent } from 'react';
import React, { useState, useCallback, useMemo } from 'react';
import { z } from 'zod';

import { useAppContext } from '../../contexts';
import { useTranslations } from '../../hooks';
import { BackInStockNotificationChannel } from '../../models';
import { NOTIFY_FORM, NOTIFY_FORM_INPUT, NOTIFY_FORM_SUBMIT } from './constants';
import { Field, Form, Root, SubmitButton } from './styles';
import type { NotifyFormProps } from './types';

const DATA_TESTID = 'data-testid';

export const NotifyForm: FC<NotifyFormProps> = ({ currentType, inputData, onSubmit, onChange, productCode }) => {
  const { getTestId } = useTestId();
  const { subscribe } = useAppContext();
  const { translations } = useTranslations();
  const isEmail: boolean = BackInStockNotificationChannel.Email === currentType;

  const isCorrectInputData: boolean = isEmail
    ? z.string().email().safeParse(inputData).success
    : z
        .string()
        .regex(/^\+?\d+$/)
        .safeParse(inputData).success;

  const isInvalid = !inputData.length || !isCorrectInputData;
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isSubmittedWithInvalidData = isInvalid && isSubmitted;

  const errorLabel = useMemo(
    () => (isEmail ? translations.invalidEmail : translations.invalidTelephoneNumber),
    [isEmail, translations.invalidEmail, translations.invalidTelephoneNumber],
  );

  const onClick = useCallback(
    async (event: SyntheticEvent) => {
      event.preventDefault();
      setIsSubmitted(true);

      if (!isCorrectInputData) {
        return;
      }

      try {
        setSubmitLoading(true);
        await subscribe(
          isEmail
            ? {
                notificationChannel: BackInStockNotificationChannel.Email,
                itemCode: productCode,
                email: inputData,
              }
            : {
                notificationChannel: BackInStockNotificationChannel.Sms,
                itemCode: productCode,
                telephoneNumber: inputData,
              },
        );

        onSubmit();
      } catch (error) {
        // eslint-disable-next-line no-console -- error log
        console.log(error);
      } finally {
        setSubmitLoading(false);
      }
    },
    [isCorrectInputData, subscribe, isEmail, productCode, inputData, onSubmit],
  );

  return (
    <Root data-testid={getTestId(NOTIFY_FORM)}>
      <Form>
        <Field>
          <TextField
            required
            label={currentType}
            variant="standard"
            type="text"
            value={inputData}
            error={isSubmittedWithInvalidData}
            helperText={isSubmittedWithInvalidData ? errorLabel : undefined}
            inputProps={{ [DATA_TESTID]: getTestId(NOTIFY_FORM_INPUT) }}
            onChange={onChange}
          />
        </Field>
      </Form>
      <SubmitButton
        fullWidth
        variant="contained"
        color="primary"
        data-testid={getTestId(NOTIFY_FORM_SUBMIT)}
        loading={submitLoading}
        onClick={onClick}
      >
        {translations.submitNotification}
      </SubmitButton>
    </Root>
  );
};
